import { checkOrgSlug } from '@mapshare/core';

/**
 * @fileoverview Which organization the page is open under, read off the URL
 * the same two ways the API reads it (docs/04-architecture.md 4.11,
 * apps/api/src/tenant.ts).
 *
 *     host   mycompany.mapshare.fyi/settings        → API /api/v1/…
 *            mycompany.localhost:8787/settings      (dev)
 *     path   staging.mapshare.fyi/mycompany/settings → API /mycompany/api/v1/…
 *            <preview>.workers.dev/mycompany/settings
 *
 * **The server decides; this only builds URLs.** Nothing here grants access. If
 * this guesses a mode the deployment does not run, the API answers 404 and the
 * page says so — it cannot reach a different organization, because the API
 * reads the organization from the request it receives, and checks the
 * membership itself.
 *
 * **Host mode is a single label in front of a known suffix, and that label
 * has to be a slug an organization could have.** `localhost` is dev;
 * `mapshare.fyi` is production since 2026-09-25 (F-B-11). A reserved label
 * falls through to path mode, which is what keeps `staging.mapshare.fyi/<slug>/`
 * (OQ-16) and the old `app.mapshare.fyi/<slug>/` working: `staging` and `app`
 * are reserved words (invariant 6), so they can never be read as an
 * organization. In production the Worker redirects `app.mapshare.fyi/<slug>/…`
 * before this page loads; reading it as path mode matters only if production
 * is rolled back to path mode (docs/setup.md 8).
 */

export interface Tenant {
  slug: string;
  /** Prefix for this organization's screens: `''` in host mode, `/<slug>` in path mode. */
  appBase: string;
  /** Prefix for this organization's API: `/api/v1` or `/<slug>/api/v1`. */
  apiBase: string;
}

/** Hosts whose first label is the organization, when it could be one. */
export const HOST_MODE_SUFFIXES: readonly string[] = ['.localhost', '.mapshare.fyi'];

function hostModeSlug(hostname: string): string | null {
  for (const suffix of HOST_MODE_SUFFIXES) {
    if (!hostname.endsWith(suffix)) continue;
    const label = hostname.slice(0, -suffix.length);
    if (label.includes('.')) return null;
    const checked = checkOrgSlug(label);
    return checked.ok ? checked.slug : null;
  }
  return null;
}

export function tenantFromLocation(location: {
  hostname: string;
  pathname: string;
}): Tenant | null {
  const hostSlug = hostModeSlug(location.hostname.toLowerCase());
  if (hostSlug !== null) return { slug: hostSlug, appBase: '', apiBase: '/api/v1' };

  const first = location.pathname.split('/')[1] ?? '';
  const checked = checkOrgSlug(first);
  if (!checked.ok) return null;
  const base = `/${checked.slug}`;
  return { slug: checked.slug, appBase: base, apiBase: `${base}/api/v1` };
}

/** The path below the organization: `/mycompany/register` → `/register`. */
export function pathWithinTenant(tenant: Tenant, pathname: string): string {
  if (tenant.appBase === '') return pathname;
  const rest = pathname.slice(tenant.appBase.length);
  return rest.length === 0 ? '/' : rest;
}

/**
 * The organization's address as a person would type it: `mycompany.mapshare.fyi/`
 * where the organization is a subdomain (production), `staging.mapshare.fyi/mycompany/`
 * where it is a path (staging, preview). `null` outside an organization.
 */
export function organizationAddress(location: {
  host: string;
  hostname: string;
  pathname: string;
}): string | null {
  const tenant = tenantFromLocation(location);
  if (tenant === null) return null;
  return `${location.host}${tenant.appBase}/`;
}
